import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Printer } from "lucide-react";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency, formatDate, formatQuantity } from "@/lib/format";
import { usePricingRole } from "@/hooks/use-pricing-role";

export const Route = createFileRoute("/_authenticated/invoices/$id_/pdf")({
  head: () => ({ meta: [
    { title: "Faktura-PDF | VERQO" },
    { name: "description", content: "Kundevendt fakturadokument." },
    { property: "og:title", content: "Faktura-PDF | VERQO" },
    { property: "og:description", content: "Kundevendt fakturadokument." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: InvoicePdf,
});

function InvoicePdf() {
  const { id } = Route.useParams();
  const { canManagePricing, isLoading: roleLoading } = usePricingRole();

  const query = useQuery({
    queryKey: ["invoice-pdf", id],
    enabled: canManagePricing,
    queryFn: async () => {
      const [{ data: invoice, error }, { data: lines, error: lineError }] = await Promise.all([
        supabase.from("invoice_document_header_v").select("invoice_id,invoice_number,document_type,credit_of_invoice_id,title,description,presentation,reference_name,issue_date,issued_at,delivery_date,due_date,payment_terms_days,subtotal_excl_vat,vat_amount,total_incl_vat,vat_percent,bill_to_name,bill_to_org_number,bill_to_address,bill_to_postal_code,bill_to_city,site_address,site_postal_code,site_city,site_contact_name,site_contact_phone,terms_payment,terms_included,terms_excluded,terms_reservations,seller_name,seller_org_number,seller_address,seller_postal_code,seller_city,seller_phone,seller_email,seller_bank_account,seller_iban,seller_vat_registered,seller_logo_storage_path").eq("invoice_id", id).single(),
        supabase.from("invoice_document_line_v").select("line_id,invoice_id,description,quantity,unit,unit_price,line_total_excl_vat,sort_order").eq("invoice_id", id).order("sort_order"),
      ]);
      if (error) throw error;
      if (lineError) throw lineError;
      let originalNumber: number | null = null;
      if (invoice.credit_of_invoice_id) {
        const { data: original } = await supabase.from("invoice_document_header_v").select("invoice_number").eq("invoice_id", invoice.credit_of_invoice_id).maybeSingle();
        originalNumber = original?.invoice_number ?? null;
      }
      return { invoice, lines: lines ?? [], originalNumber };
    },
  });

  useEffect(() => { if (query.data) setTimeout(() => window.print(), 400); }, [query.data]);

  if (roleLoading) return <div className="p-10 text-center">Laster…</div>;
  if (!canManagePricing) return <div className="p-10 text-center">Ingen tilgang.</div>;
  if (!query.data) return <div className="p-10 text-center">Laster…</div>;

  const { invoice, lines, originalNumber } = query.data;
  const logo = invoice.seller_logo_storage_path?.startsWith("http") ? invoice.seller_logo_storage_path : null;
  const title = invoice.document_type === "credit_note" ? "KREDITNOTA" : "FAKTURA";

  return <>
    <style>{`@page{size:A4;margin:18mm}@media print{.no-print{display:none!important}body{background:white!important}}`}</style>
    <div className="no-print sticky top-0 flex justify-between border-b bg-card p-3">
      <Button asChild variant="ghost"><Link to="/invoices/$id" params={{ id }}><ArrowLeft className="h-4 w-4"/>Tilbake</Link></Button>
      <Button onClick={() => window.print()}><Printer className="h-4 w-4"/>Skriv ut / lagre PDF</Button>
    </div>
    <article className="mx-auto my-8 max-w-[210mm] bg-white p-12 text-neutral-900 shadow-xl print:my-0 print:shadow-none">
      <header className="flex justify-between gap-8 border-b-2 border-neutral-900 pb-8">
        <div>
          {logo && <img src={logo} alt={invoice.seller_name ?? ""} className="mb-4 max-h-20 max-w-52 object-contain"/>}
          <div className="font-semibold">{invoice.seller_name}</div>
          <div className="text-sm text-neutral-600">Org.nr {invoice.seller_org_number}{invoice.seller_vat_registered ? " MVA" : ""}</div>
          <div className="text-sm text-neutral-600">{invoice.seller_address}</div>
          <div className="text-sm text-neutral-600">{invoice.seller_postal_code} {invoice.seller_city}</div>
          <div className="text-sm text-neutral-600">Telefon: {invoice.seller_phone}</div>
          <div className="text-sm text-neutral-600">{invoice.seller_email}</div>
        </div>
        <div className="text-right">
          <h1 className="text-3xl font-semibold">{title}</h1>
          <div className="mt-3 text-sm">Fakturanr: <b>{invoice.invoice_number}</b></div>
          <div className="text-sm">Fakturadato: {formatDate(invoice.issue_date)}</div>
          <div className="text-sm">Forfallsdato: {formatDate(invoice.due_date)}</div>
          {invoice.reference_name && <div className="text-sm">Vår ref: {invoice.reference_name}</div>}
        </div>
      </header>
      {invoice.document_type === "credit_note" && <div className="mt-6 border border-neutral-300 p-3 font-semibold">Kreditnota til faktura {originalNumber}</div>}
      <section className="grid grid-cols-2 gap-10 py-8">
        <div>
          <Label>Fakturamottaker</Label>
          <div className="font-semibold">{invoice.bill_to_name}</div>
          {invoice.bill_to_org_number && <div className="text-sm">Org.nr {invoice.bill_to_org_number}</div>}
          <div className="text-sm">{invoice.bill_to_address}</div>
          <div className="text-sm">{invoice.bill_to_postal_code} {invoice.bill_to_city}</div>
        </div>
        <div>
          <Label>Arbeidssted</Label>
          <div className="text-sm">{invoice.site_address || "—"}</div>
          <div className="text-sm">{invoice.site_postal_code} {invoice.site_city}</div>
          {invoice.site_contact_name && <div className="mt-1 text-sm">Kontakt: {invoice.site_contact_name}{invoice.site_contact_phone ? ` · ${invoice.site_contact_phone}` : ""}</div>}
          <div className="mt-3 text-sm">Leveringsdato: {formatDate(invoice.delivery_date || invoice.issue_date)}</div>
        </div>
      </section>
      {invoice.description && <section className="mb-8"><Label>Beskrivelse av oppdraget</Label><p className="whitespace-pre-wrap text-sm">{invoice.description}</p></section>}
      <table className="w-full text-sm">
        <thead><tr className="border-b border-neutral-900 text-left"><th className="py-2">Post / beskrivelse</th><th className="text-right">Antall</th><th className="text-right">Enhet</th><th className="text-right">Pris</th><th className="text-right">Sum</th></tr></thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.line_id} className="border-b border-neutral-200">
              <td className="py-2">{line.description}</td>
              <td className="text-right">{formatQuantity(Number(line.quantity), line.unit ?? "")}</td>
              <td className="text-right">{line.unit}</td>
              <td className="text-right">{formatCurrency(Number(line.unit_price))}</td>
              <td className="text-right">{formatCurrency(Number(line.line_total_excl_vat))}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <section className="ml-auto mt-8 w-80">
        <Sum label="Sum eks. mva" value={formatCurrency(Number(invoice.subtotal_excl_vat))}/>
        <Sum label={`MVA ${invoice.vat_percent} %`} value={formatCurrency(Number(invoice.vat_amount))}/>
        <div className="my-2 border-t border-neutral-900"/>
        <Sum label="Å betale" value={formatCurrency(Number(invoice.total_incl_vat))} strong/>
      </section>
      {invoice.document_type === "invoice" && (
        <section className="mt-10 border-t pt-6">
          <Label>Betaling</Label>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span>Kontonummer</span><b>{invoice.seller_bank_account || "Ikke oppgitt"}</b>
            <span>Betalingsfrist</span><b>{formatDate(invoice.due_date)}</b>
            {invoice.seller_iban && <><span>IBAN</span><b>{invoice.seller_iban}</b></>}
          </div>
          <p className="mt-3 text-sm">Merk betalingen med fakturanummer {invoice.invoice_number}.</p>
          <p className="mt-1 text-xs text-neutral-600">Ved forsinket betaling påløper forsinkelsesrente etter forsinkelsesrenteloven.</p>
        </section>
      )}
      {[["Dette er inkludert", invoice.terms_included], ["Dette er ikke inkludert", invoice.terms_excluded], ["Forbehold", invoice.terms_reservations], ["Betalingsbetingelser", invoice.terms_payment]]
        .filter(([, body]) => body?.trim())
        .map(([heading, body]) => <section key={heading} className="mt-6"><Label>{heading}</Label><p className="whitespace-pre-wrap text-sm">{body}</p></section>)}
    </article>
  </>;
}

function Label({ children }: { children: React.ReactNode }) {
  return <div className="mb-2 text-xs font-semibold uppercase text-neutral-500">{children}</div>;
}

function Sum({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return <div className={`flex justify-between py-1 ${strong ? "text-lg font-semibold" : "text-sm"}`}><span>{label}</span><span>{value}</span></div>;
}
